import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch music track events (kind 36787) for a specific pubkey
 */
export function useMusic(pubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['music', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query([
        {
          kinds: [36787], // Music track events
          authors: [pubkey],
          limit: 50,
        }
      ], { signal });

      // Filter valid music events
      return events
        .filter(validateMusicEvent)
        .sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!pubkey && enabled,
    staleTime: 0, // Always consider data stale to refetch on tab switch
  });
}

/**
 * Validator for music track events
 */
export function validateMusicEvent(event: NostrEvent): boolean {
  if (event.kind !== 36787) return false;

  // Required tags
  const hasD = event.tags.some(([name]) => name === 'd');
  const hasTitle = event.tags.some(([name]) => name === 'title');
  const hasArtist = event.tags.some(([name]) => name === 'artist');
  const hasUrl = event.tags.some(([name]) => name === 'url');
  const hasMusic = event.tags.some(([name, value]) => name === 't' && value === 'music');

  return hasD && hasTitle && hasArtist && hasUrl && hasMusic;
}

/**
 * Extract track title from music event
 */
export function extractMusicTitle(event: NostrEvent): string | null {
  const titleTag = event.tags.find(([name]) => name === 'title');
  return titleTag?.[1] || null;
}

/**
 * Extract artist name from music event
 */
export function extractMusicArtist(event: NostrEvent): string | null {
  const artistTag = event.tags.find(([name]) => name === 'artist');
  return artistTag?.[1] || null;
}

/**
 * Extract audio URL from music event
 */
export function extractMusicUrl(event: NostrEvent): string | null {
  const urlTag = event.tags.find(([name]) => name === 'url');
  return urlTag?.[1] || null;
}

/**
 * Extract album artwork URL from music event
 */
export function extractMusicImage(event: NostrEvent): string | null {
  const imageTag = event.tags.find(([name]) => name === 'image');
  return imageTag?.[1] || null;
}

/**
 * Extract duration from music event
 */
export function extractMusicDuration(event: NostrEvent): number | null {
  const durationTag = event.tags.find(([name]) => name === 'duration');
  if (!durationTag?.[1]) return null;

  const duration = parseInt(durationTag[1]);
  return isNaN(duration) ? null : duration;
}
